import {AllSectionTypes, CmsPage, CmsPages, CmsSection, ModuleCms, WebappCms} from "@lib/Cms/CmsModels";
import {useAppContextStore} from "@store/AppContextStore";

type Maybe<T> = T | null | undefined;

export type SectionMatch =
    | AllSectionTypes
    | ((section: CmsSection) => boolean)
    | Partial<Pick<CmsSection, "type" | "enabled" | "title">>;

export function getCmsPage(pageId: string, pages?: ModuleCms): CmsPage | null {

    const {cms} = useAppContextStore.getState()

    const pageWrapper = !!pages ? pages.configuration?.[pageId] : cms?.configuration.pages?.[pageId];

    if (!pageWrapper || !pageWrapper.enabled || !pageWrapper.page?.enabled) {
        return null;
    }

    return pageWrapper.page;

}

export function mergePages(...sources: Array<CmsPages | undefined>): CmsPages {
    const out: CmsPages = {};
    for (const src of sources) {
        if (!src) continue;
        Object.assign(out, src);
    }
    return out;
}

export function mergeGlobalWithModules(globalCms: WebappCms, ...modulePages: CmsPages[]): WebappCms {
    const globalPages = globalCms.configuration?.pages ?? {};
    const pages = mergePages(globalPages, ...modulePages);

    return {
        ...globalCms,
        configuration: {
            ...globalCms.configuration,
            pages,
        },
    };
}

export function isSectionEnabled(section?: Maybe<CmsSection>): boolean {
    return !!section && (section.enabled ?? true);
}

export function findSections(
    pageOrSections: Maybe<CmsPage | CmsSection[]>,
    match: SectionMatch,
    includeDisabled = false
): CmsSection[] {

    const all: CmsSection[] =
        Array.isArray(pageOrSections)
            ? (pageOrSections ?? [])
            : (pageOrSections?.sections ?? []);

    if (typeof match === "string") {
        return all.filter((s) => s.type === match && (includeDisabled ? true : isSectionEnabled(s)));
    }

    if (typeof match === "function") {
        return all.filter(match);
    }

    return all.filter((s) => {
        if (match.type != null && s.type !== match.type) return false;
        if (match.enabled != null && (s.enabled ?? true) !== match.enabled) return false;
        return !(match.title != null && s.title !== match.title);
    });
}

export function findFirstSection(
    pageOrSections: Maybe<CmsPage | CmsSection[]>,
    match: SectionMatch,
    includeDisabled = false,
): CmsSection | undefined {
    const matched = findSections(pageOrSections, match, includeDisabled);
    return matched[0] ?? undefined;
}
